/*
 * Copyright (c) 2024 Helvia Technologies PC <contact@helvia.io>
 */

import { useCallback, useEffect, useState } from 'react';
import { SHAREPOINT_CLIENT_ID } from '../../../constants/env';

/**
 * Manages the SharePoint admin-consent OAuth flow.
 *
 * Opens a popup for Azure AD admin consent and listens for the
 * resulting tenant ID via `postMessage` from the callback page.
 *
 * @param {object|undefined} integration - The integration object (needs `tenantId` and `type`).
 * @returns {{ consentTenantId: string, handleSharePointConsent: () => void }}
 */
export const useSharePointConsent = (integration) => {
  const [consentTenantId, setConsentTenantId] = useState(integration?.tenantId || '');

  const handleSharePointConsent = useCallback(() => {
    const redirectUri = `${window.location.origin}/integrations/sharepoint/callback`;
    const state = crypto.randomUUID();
    const consentUrl
      = `https://login.microsoftonline.com/common/adminconsent`
      + `?client_id=${encodeURIComponent(SHAREPOINT_CLIENT_ID)}`
      + `&redirect_uri=${encodeURIComponent(redirectUri)}`
      + `&state=${state}`;
    window.open(consentUrl, 'sharepoint-consent', 'width=600,height=700');
  }, []);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      if (event.data?.type === 'sharepoint-consent' && event.data?.tenantId) {
        setConsentTenantId(event.data.tenantId);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return { consentTenantId, handleSharePointConsent };
};
